import { useState, useEffect, useCallback } from "react";
import { 
  ShieldCheck, FileCheck, Upload, MessageCircle, Mail, Phone, Building2,
  Calendar, Search, Eye, CheckCircle2, AlertTriangle, Clock, XCircle,
  FileText, Download, Send, ChevronRight, UserPlus, Key, Lock, Paperclip
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axCfg = { withCredentials: true };

// Estados de validación
const STATUS_CONFIG = {
  pendiente: { label: "Pendiente", color: "bg-gray-100 text-gray-700", icon: Clock },
  en_revision: { label: "En Revisión", color: "bg-blue-100 text-blue-700", icon: Eye },
  documentos_completos: { label: "Documentos Completos", color: "bg-green-100 text-green-700", icon: CheckCircle2 },
  rechazado: { label: "Rechazado", color: "bg-red-100 text-red-700", icon: XCircle },
};

// Documentos requeridos por tipo de servicio
const DOCUMENTOS_REQUERIDOS = {
  "Permisos de Autoconsumo": [
    { id: "constancia_fiscal", nombre: "Constancia de Situación Fiscal", obligatorio: true },
    { id: "identificacion_rep", nombre: "INE del Representante Legal", obligatorio: true },
    { id: "acta_constitutiva", nombre: "Acta Constitutiva", obligatorio: true },
    { id: "poder_notarial", nombre: "Poder Notarial del Representante", obligatorio: true },
    { id: "comprobante_domicilio", nombre: "Comprobante de Domicilio", obligatorio: true },
    { id: "escrituras_predio", nombre: "Escrituras o Contrato del Predio", obligatorio: true },
    { id: "memoria_tecnica", nombre: "Memoria Técnica", obligatorio: false },
    { id: "planos_instalacion", nombre: "Planos de Instalación", obligatorio: false },
    { id: "dictamen_proteccion_civil", nombre: "Dictamen Protección Civil", obligatorio: false },
    { id: "uso_suelo", nombre: "Licencia de Uso de Suelo", obligatorio: false },
    { id: "factura_tanque", nombre: "Factura del Tanque", obligatorio: false },
  ],
  "Controles Volumétricos": [
    { id: "constancia_fiscal", nombre: "Constancia de Situación Fiscal", obligatorio: true },
    { id: "identificacion_rep", nombre: "INE del Representante Legal", obligatorio: true },
    { id: "permiso_cre", nombre: "Permiso CRE Vigente", obligatorio: true },
    { id: "e_firma", nombre: "e.Firma (FIEL)", obligatorio: true },
    { id: "sellos_digitales", nombre: "Sellos Digitales SAT", obligatorio: true },
    { id: "diagrama_instalacion", nombre: "Diagrama de Instalación", obligatorio: false },
    { id: "calibracion_equipos", nombre: "Certificado Calibración Equipos", obligatorio: false },
  ],
  "Gestión Integral": [
    { id: "constancia_fiscal", nombre: "Constancia de Situación Fiscal", obligatorio: true },
    { id: "identificacion_rep", nombre: "INE del Representante Legal", obligatorio: true },
    { id: "acta_constitutiva", nombre: "Acta Constitutiva", obligatorio: true },
    { id: "poder_notarial", nombre: "Poder Notarial", obligatorio: true },
    { id: "comprobante_domicilio", nombre: "Comprobante de Domicilio", obligatorio: true },
    { id: "escrituras_predio", nombre: "Escrituras o Contrato del Predio", obligatorio: true },
    { id: "permiso_cre", nombre: "Permiso CRE (si aplica)", obligatorio: false },
    { id: "poliza_seguro", nombre: "Póliza de Seguro", obligatorio: false },
  ],
  "Suministro de Combustible": [
    { id: "constancia_fiscal", nombre: "Constancia de Situación Fiscal", obligatorio: true },
    { id: "identificacion_rep", nombre: "INE del Representante Legal", obligatorio: true },
    { id: "comprobante_domicilio", nombre: "Comprobante de Domicilio Fiscal", obligatorio: true },
    { id: "referencias_comerciales", nombre: "Referencias Comerciales", obligatorio: false },
    { id: "estados_financieros", nombre: "Estados Financieros (para crédito)", obligatorio: false },
  ],
};

// Documentos genéricos
const DOCUMENTOS_GENERICOS = [
  { id: "constancia_fiscal", nombre: "Constancia de Situación Fiscal", obligatorio: true },
  { id: "identificacion_rep", nombre: "INE del Representante Legal", obligatorio: true },
  { id: "comprobante_domicilio", nombre: "Comprobante de Domicilio", obligatorio: true },
];

export default function ValidacionTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  
  // Modales
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [documentosChecked, setDocumentosChecked] = useState({});
  const [observaciones, setObservaciones] = useState("");
  const [saving, setSaving] = useState(false);
  
  // Modal Crear Login
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [loginItem, setLoginItem] = useState(null);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [creatingLogin, setCreatingLogin] = useState(false);

  // Modal Subir Contrato
  const [contratoModalOpen, setContratoModalOpen] = useState(false);
  const [contratoItem, setContratoItem] = useState(null);
  const [archivoContrato, setArchivoContrato] = useState(null);
  const [subiendoContrato, setSubiendoContrato] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      // Obtener pre-trámites aprobados que pasan a validación
      const res = await axios.get(`${API}/admin/validaciones`, axCfg);
      setData(res.data);
    } catch (e) {
      // Si el endpoint no existe, mostrar datos vacíos
      console.log("Endpoint de validaciones no encontrado, mostrando vacío");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Abrir detalle
  const openDetalle = (item) => {
    setSelectedItem(item);
    setDocumentosChecked(item.documentos_validados || {});
    setObservaciones(item.observaciones_validacion || "");
    setDetalleOpen(true);
  };

  // Obtener documentos según tipo de servicio
  const getDocumentosRequeridos = (tipoServicio) => {
    return DOCUMENTOS_REQUERIDOS[tipoServicio] || DOCUMENTOS_GENERICOS;
  };

  // Calcular progreso de documentos
  const calcularProgreso = (item) => {
    const docs = getDocumentosRequeridos(item.service_type);
    const docsObligatorios = docs.filter(d => d.obligatorio);
    const docsValidados = item.documentos_validados || {};
    const completados = docsObligatorios.filter(d => docsValidados[d.id] === true).length;
    return Math.round((completados / docsObligatorios.length) * 100);
  };

  // Guardar validación
  const saveValidacion = async (nuevoEstado = null) => {
    if (!selectedItem) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/validaciones/${selectedItem.id}`, {
        documentos_validados: documentosChecked,
        observaciones_validacion: observaciones,
        estado_validacion: nuevoEstado || selectedItem.estado_validacion,
      }, axCfg);
      toast.success("Validación guardada");
      setDetalleOpen(false);
      fetchData();
    } catch (e) {
      toast.error("Error al guardar validación");
    } finally {
      setSaving(false);
    }
  };

  // Solicitar documentos faltantes por WhatsApp
  const solicitarDocumentos = (item) => {
    const docs = getDocumentosRequeridos(item.service_type);
    const docsValidados = item.documentos_validados || {};
    const faltantes = docs.filter(d => d.obligatorio && !docsValidados[d.id]);
    
    if (faltantes.length === 0) {
      toast.info("Todos los documentos obligatorios están completos");
      return;
    }
    
    const phone = (item.whatsapp || item.phone || "").replace(/[^0-9]/g, "");
    if (!phone) {
      toast.error("No hay número de teléfono");
      return;
    }
    
    const listaFaltantes = faltantes.map(d => `- ${d.nombre}`).join("\n");
    const msg = `*PETROCONTROL - Documentos Pendientes*\n\nEstimado/a ${item.full_name},\n\nPara continuar con su trámite de ${item.service_type}, requerimos los siguientes documentos:\n\n${listaFaltantes}\n\nPor favor envíelos a la brevedad para agilizar su proceso.\n\nGracias.\n_Equipo PETROCONTROL_`;
    
    window.open(`https://wa.me/52${phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  // Abrir modal de crear login
  const openLoginModal = (item) => {
    setLoginItem(item);
    setLoginEmail(item.email || "");
    setLoginPassword("");
    setLoginModalOpen(true);
  };

  // Generar contraseña aleatoria
  const generarPasswordAleatorio = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    let password = "";
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setLoginPassword(password);
  };

  // Crear login del cliente
  const crearLoginCliente = async () => {
    if (!loginItem) return;
    if (!loginEmail || !loginPassword) {
      toast.error("Email y contraseña son requeridos");
      return;
    }
    if (loginPassword.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    
    setCreatingLogin(true);
    try {
      await axios.post(`${API}/admin/validaciones/${loginItem.id}/crear-login`, {
        email: loginEmail,
        password: loginPassword
      }, axCfg);
      toast.success("Login creado exitosamente. El cliente puede acceder al portal.");
      setLoginModalOpen(false);
      // Guardar credenciales para envío
      setCredencialesParaEnviar({ email: loginEmail, password: loginPassword, item: loginItem });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear login");
    } finally {
      setCreatingLogin(false);
    }
  };

  // Estado para credenciales recién creadas
  const [credencialesParaEnviar, setCredencialesParaEnviar] = useState(null);

  // Descargar PDF de credenciales
  const descargarPDFCredenciales = async (item, password = null) => {
    try {
      // Si tenemos password, la pasamos como query param
      const url = password 
        ? `${API}/admin/validaciones/${item.id}/credenciales-pdf?password=${encodeURIComponent(password)}`
        : `${API}/admin/validaciones/${item.id}/credenciales-pdf`;
      
      const response = await axios.get(url, {
        ...axCfg,
        responseType: 'blob'
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `credenciales_${item.cliente_email || item.full_name}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.success("PDF de credenciales descargado");
    } catch (e) {
      toast.error("Error al descargar PDF de credenciales");
    }
  };

  // Enviar credenciales por email (mailto)
  const enviarCredencialesPorEmail = (item, password = null) => {
    const email = item.cliente_email || item.email;
    if (!email) {
      toast.error("No hay email del cliente");
      return;
    }
    
    const portalUrl = window.location.origin + "/portal";
    const subject = encodeURIComponent("PETROCONTROL - Credenciales de Acceso al Portal");
    const body = encodeURIComponent(`Estimado/a ${item.full_name},

Nos es grato proporcionarle sus credenciales de acceso al Portal de Clientes PETROCONTROL:

━━━━━━━━━━━━━━━━━━━━━━━━━━━━
DATOS DE ACCESO
━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Usuario (Email): ${email}
${password ? `Contraseña: ${password}` : 'Contraseña: (la contraseña fue proporcionada por su ejecutivo)'}

URL de Acceso: ${portalUrl}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━

INSTRUCCIONES:
1. Ingrese a la URL del portal
2. Introduzca su email y contraseña
3. Podrá ver sus trámites activos y subir documentación

Por seguridad, le recomendamos cambiar su contraseña después del primer acceso.

Quedamos a sus órdenes.

Saludos cordiales,
Equipo PETROCONTROL

━━━━━━━━━━━━━━━━━━━━━━━━━━━━
PETROCONTROL
Confianza. Precisión. Control.
www.petrocontrol.mx
━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
    
    window.open(`mailto:${email}?subject=${subject}&body=${body}`, "_blank");
    toast.success("Se abrió el cliente de correo para enviar credenciales");
  };

  // Abrir modal para adjuntar contrato
  const abrirModalContrato = (item) => {
    // BLOQUEO: No permitir generar contrato si no tiene login creado
    if (!item.cliente_login_creado) {
      toast.error("No es posible validar sin haber creado login de usuario y contraseña para el cliente. Use el botón 'Crear Login' primero.");
      return;
    }
    
    setContratoItem(item);
    setArchivoContrato(null);
    setContratoModalOpen(true);
  };

  // Manejar selección de archivo
  const handleArchivoContratoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const ext = file.name.toLowerCase();
      if (!ext.endsWith('.doc') && !ext.endsWith('.docx') && !ext.endsWith('.pdf')) {
        toast.error("Solo se permiten archivos .doc, .docx o .pdf");
        return;
      }
      setArchivoContrato(file);
    }
  };

  // Subir contrato y avanzar a siguiente etapa
  const subirContratoYAvanzar = async () => {
    if (!contratoItem || !archivoContrato) {
      toast.error("Debe adjuntar el archivo de contrato antes de continuar");
      return;
    }
    
    setSubiendoContrato(true);
    try {
      // 1. Subir el archivo
      const formData = new FormData();
      formData.append('file', archivoContrato);
      
      const uploadRes = await axios.post(
        `${API}/admin/validaciones/${contratoItem.id}/subir-contrato`,
        formData,
        { ...axCfg, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      // 2. Crear el contrato con el archivo adjunto
      await axios.post(`${API}/admin/contratos`, {
        validacion_id: contratoItem.id,
        prospecto_id: contratoItem.prospect_id,
        cliente_id: contratoItem.cliente_id,
        cliente_nombre: contratoItem.company_name || contratoItem.full_name,
        servicio: contratoItem.service_type,
        monto_total: contratoItem.monto_cotizacion || 0,
        archivo_contrato: uploadRes.data.archivo_path,
        archivo_contrato_nombre: archivoContrato.name,
      }, axCfg);
      
      toast.success("Contrato adjuntado y guardado. Pasó a pestaña Contratos.");
      setContratoModalOpen(false);
      setArchivoContrato(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al subir contrato");
    } finally {
      setSubiendoContrato(false);
    }
  };

  // Filtrar datos
  const filteredData = data.filter(item => {
    const matchSearch = !search || 
      item.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      item.company_name?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === "all" || item.estado_validacion === filterStatus;
    return matchSearch && matchStatus;
  });

  if (loading) return <p className="text-center py-12 text-gray-500">Cargando validaciones...</p>;

  return (
    <div className="space-y-4" data-testid="validacion-tab">
      {/* Métricas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-none shadow-sm">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gray-500 flex items-center justify-center">
                <Clock className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-xl font-bold text-[#1a1a2e]">{data.filter(d => d.estado_validacion === "pendiente").length}</p>
                <p className="text-xs text-gray-600">Pendientes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                <Eye className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-xl font-bold text-[#1a1a2e]">{data.filter(d => d.estado_validacion === "en_revision").length}</p>
                <p className="text-xs text-gray-600">En Revisión</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-green-500 flex items-center justify-center">
                <CheckCircle2 className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-xl font-bold text-[#1a1a2e]">{data.filter(d => d.estado_validacion === "documentos_completos").length}</p>
                <p className="text-xs text-gray-600">Completos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#1a5c4a] flex items-center justify-center">
                <ShieldCheck className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-xl font-bold text-[#1a1a2e]">{data.length}</p>
                <p className="text-xs text-gray-600">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 bg-white rounded-xl p-3 shadow-sm">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar por nombre o empresa..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
        
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <span className="text-sm text-gray-500">{filteredData.length} registros</span>
      </div>

      {/* Lista */}
      {filteredData.length === 0 ? (
        <Card className="border-none shadow-sm">
          <CardContent className="text-center py-16">
            <ShieldCheck className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No hay registros en validación</p>
            <p className="text-sm text-gray-400 mt-1">Los pre-trámites aprobados aparecerán aquí para validar documentación</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {filteredData.map((item) => {
            const statusCfg = STATUS_CONFIG[item.estado_validacion] || STATUS_CONFIG.pendiente;
            const StatusIcon = statusCfg.icon;
            const progreso = calcularProgreso(item);
            
            return (
              <Card key={item.id} className="border-none shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-semibold text-gray-900">{item.full_name}</h3>
                        <Badge className={statusCfg.color}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusCfg.label}
                        </Badge>
                        {/* Badge de Login */}
                        {item.cliente_login_creado ? (
                          <Badge className="bg-green-100 text-green-700">
                            <Key className="h-3 w-3 mr-1" />
                            Login Activo
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700">
                            <Lock className="h-3 w-3 mr-1" />
                            Sin Login
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4 text-xs text-gray-600 mb-3 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Building2 className="h-3 w-3" />
                          {item.company_name || "Sin empresa"}
                        </span>
                        <span className="font-medium text-[#1a5c4a]">{item.service_type}</span>
                        {item.monto_cotizacion && (
                          <span className="font-semibold text-emerald-600">
                            ${item.monto_cotizacion.toLocaleString()}
                          </span>
                        )}
                        {item.cliente_email && (
                          <span className="text-blue-600">
                            Portal: {item.cliente_email}
                          </span>
                        )}
                      </div>
                      
                      {/* Barra de progreso */}
                      <div className="flex items-center gap-3">
                        <Progress value={progreso} className="flex-1 h-2" />
                        <span className="text-xs font-medium text-gray-600">{progreso}%</span>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 flex-shrink-0">
                      {/* Botón Crear Login */}
                      {!item.cliente_login_creado && (
                        <Button
                          size="sm"
                          className="bg-amber-500 hover:bg-amber-600 text-white"
                          onClick={() => openLoginModal(item)}
                          data-testid="crear-login-btn"
                        >
                          <UserPlus className="h-4 w-4 mr-1" />
                          Crear Login
                        </Button>
                      )}
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openDetalle(item)}
                      >
                        <FileCheck className="h-4 w-4 mr-1" />
                        Validar
                      </Button>
                      
                      {progreso === 100 && item.estado_validacion !== "documentos_completos" && (
                        <Button
                          size="sm"
                          className={`${item.cliente_login_creado ? 'bg-[#1a5c4a] hover:bg-[#164a3d]' : 'bg-gray-300 cursor-not-allowed'}`}
                          onClick={() => abrirModalContrato(item)}
                          disabled={!item.cliente_login_creado}
                          title={!item.cliente_login_creado ? "Debe crear login primero" : "Adjuntar contrato y avanzar"}
                          data-testid="generar-contrato-btn"
                        >
                          <Paperclip className="h-4 w-4 mr-1" />
                          Adjuntar Contrato
                        </Button>
                      )}
                      
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-green-600 border-green-300 hover:bg-green-50"
                        onClick={() => solicitarDocumentos(item)}
                      >
                        <MessageCircle className="h-4 w-4 mr-1" />
                        Solicitar Docs
                      </Button>
                      
                      {/* Botones de envío de credenciales (solo si tiene login) */}
                      {item.cliente_login_creado && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-blue-600 border-blue-300 hover:bg-blue-50"
                            onClick={() => enviarCredencialesPorEmail(item)}
                            data-testid="enviar-credenciales-btn"
                          >
                            <Mail className="h-4 w-4 mr-1" />
                            Enviar Credenciales
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-purple-600 border-purple-300 hover:bg-purple-50"
                            onClick={() => descargarPDFCredenciales(item)}
                            data-testid="descargar-pdf-credenciales-btn"
                          >
                            <Download className="h-4 w-4 mr-1" />
                            PDF Credenciales
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Sheet de Detalle */}
      <Sheet open={detalleOpen} onOpenChange={setDetalleOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-[#1a5c4a]" />
              Validación de Documentos
            </SheetTitle>
          </SheetHeader>
          
          {selectedItem && (
            <div className="py-6 space-y-6">
              {/* Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-sm text-gray-900">{selectedItem.full_name}</h4>
                <p className="text-sm text-gray-600">{selectedItem.company_name}</p>
                <p className="text-sm text-[#1a5c4a] font-medium mt-1">{selectedItem.service_type}</p>
              </div>
              
              {/* Checklist de documentos */}
              <div>
                <h4 className="font-semibold text-sm text-gray-700 mb-3">Documentos Requeridos</h4>
                <div className="space-y-3">
                  {getDocumentosRequeridos(selectedItem.service_type).map(doc => (
                    <div key={doc.id} className="flex items-center justify-between py-2 border-b border-gray-100">
                      <div className="flex items-center gap-2">
                        <Checkbox 
                          id={doc.id}
                          checked={documentosChecked[doc.id] === true}
                          onCheckedChange={(checked) => {
                            setDocumentosChecked({
                              ...documentosChecked,
                              [doc.id]: checked
                            });
                          }}
                        />
                        <Label htmlFor={doc.id} className="text-sm cursor-pointer">
                          {doc.nombre}
                        </Label>
                      </div>
                      {doc.obligatorio && (
                        <Badge variant="outline" className="text-[10px] text-red-600 border-red-300">
                          Obligatorio
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Observaciones */}
              <div>
                <Label>Observaciones de la validación</Label>
                <Textarea
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  placeholder="Notas sobre la documentación recibida..."
                  rows={3}
                  className="mt-1"
                />
              </div>
              
              {/* Acciones */}
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => saveValidacion()}>
                  Guardar
                </Button>
                <Button 
                  variant="outline"
                  className="text-blue-600 border-blue-300"
                  onClick={() => saveValidacion("en_revision")}
                >
                  Marcar En Revisión
                </Button>
                <Button 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => saveValidacion("documentos_completos")}
                  disabled={saving}
                >
                  Documentos Completos
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Modal Crear Login */}
      <Dialog open={loginModalOpen} onOpenChange={setLoginModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-amber-500" />
              Crear Login de Cliente
            </DialogTitle>
            <DialogDescription>
              Cree las credenciales de acceso al portal para {loginItem?.full_name || "el cliente"}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {loginItem && (
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p className="font-medium text-gray-900">{loginItem.full_name}</p>
                <p className="text-gray-600">{loginItem.company_name}</p>
                <p className="text-[#1a5c4a]">{loginItem.service_type}</p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="login-email">Email de acceso *</Label>
              <Input
                id="login-email"
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="correo@ejemplo.com"
                data-testid="login-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="login-password">Contraseña *</Label>
              <div className="flex gap-2">
                <Input
                  id="login-password"
                  type="text"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  data-testid="login-password-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={generarPasswordAleatorio}
                  className="whitespace-nowrap"
                >
                  Generar
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                El cliente usará estas credenciales para acceder al portal.
              </p>
            </div>
            
            {loginPassword && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs font-medium text-amber-800 mb-1">Credenciales a compartir:</p>
                <p className="text-sm text-amber-900">Email: <strong>{loginEmail}</strong></p>
                <p className="text-sm text-amber-900">Contraseña: <strong>{loginPassword}</strong></p>
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setLoginModalOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={crearLoginCliente}
              disabled={creatingLogin || !loginEmail || !loginPassword}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="confirmar-crear-login-btn"
            >
              {creatingLogin ? "Creando..." : "Crear Login"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de envío de credenciales después de crear */}
      {credencialesParaEnviar && (
        <Dialog open={!!credencialesParaEnviar} onOpenChange={() => setCredencialesParaEnviar(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-600">
                <CheckCircle2 className="h-5 w-5" />
                Login Creado Exitosamente
              </DialogTitle>
              <DialogDescription>
                ¿Cómo desea enviar las credenciales al cliente?
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-green-800"><strong>Email:</strong> {credencialesParaEnviar.email}</p>
                <p className="text-sm text-green-800"><strong>Contraseña:</strong> {credencialesParaEnviar.password}</p>
              </div>
              
              <div className="flex flex-col gap-3">
                <Button
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  onClick={() => {
                    enviarCredencialesPorEmail({
                      ...credencialesParaEnviar.item,
                      cliente_email: credencialesParaEnviar.email,
                      full_name: credencialesParaEnviar.item?.full_name || "Cliente"
                    }, credencialesParaEnviar.password);
                    setCredencialesParaEnviar(null);
                  }}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Enviar por Email
                </Button>
                
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    descargarPDFCredenciales({
                      id: credencialesParaEnviar.item?.id,
                      cliente_email: credencialesParaEnviar.email
                    }, credencialesParaEnviar.password);
                    setCredencialesParaEnviar(null);
                  }}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Descargar PDF
                </Button>
                
                <Button
                  variant="ghost"
                  onClick={() => setCredencialesParaEnviar(null)}
                >
                  Cerrar sin enviar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal Subir Contrato */}
      <Dialog open={contratoModalOpen} onOpenChange={setContratoModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="h-5 w-5 text-[#1a5c4a]" />
              Adjuntar Contrato
            </DialogTitle>
            <DialogDescription>
              Debe adjuntar el contrato firmado (.doc, .docx o .pdf) para avanzar a la siguiente etapa.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {contratoItem && (
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p className="font-medium text-gray-900">{contratoItem.full_name}</p>
                <p className="text-gray-600">{contratoItem.company_name}</p>
                <p className="text-[#1a5c4a] font-medium">{contratoItem.service_type}</p>
                {contratoItem.monto_cotizacion && (
                  <p className="text-emerald-600 font-semibold mt-1">
                    Monto: ${contratoItem.monto_cotizacion.toLocaleString()}
                  </p>
                )}
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="archivo-contrato">Archivo de Contrato *</Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#1a5c4a] transition-colors">
                <input
                  id="archivo-contrato"
                  type="file"
                  accept=".doc,.docx,.pdf"
                  onChange={handleArchivoContratoChange}
                  className="hidden"
                  data-testid="archivo-contrato-input"
                />
                <label htmlFor="archivo-contrato" className="cursor-pointer">
                  {archivoContrato ? (
                    <div className="flex items-center justify-center gap-2 text-[#1a5c4a]">
                      <FileText className="h-8 w-8" />
                      <div className="text-left">
                        <p className="font-medium">{archivoContrato.name}</p>
                        <p className="text-xs text-gray-500">
                          {(archivoContrato.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-gray-500">
                      <Upload className="h-10 w-10 mx-auto mb-2" />
                      <p className="font-medium">Click para seleccionar archivo</p>
                      <p className="text-xs">Formatos: .doc, .docx, .pdf</p>
                    </div>
                  )}
                </label>
              </div>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-800">
                <strong>Importante:</strong> El archivo de contrato se guardará en los documentos del cliente 
                y estará disponible en la pestaña de Contratos.
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setContratoModalOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={subirContratoYAvanzar}
              disabled={subiendoContrato || !archivoContrato}
              className="bg-[#1a5c4a] hover:bg-[#164a3d]"
              data-testid="confirmar-subir-contrato-btn"
            >
              {subiendoContrato ? (
                <>Subiendo...</>
              ) : (
                <>
                  <ChevronRight className="h-4 w-4 mr-1" />
                  Subir y Avanzar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
